// API Endpoints for Bulk operations (tags and move groups)
import { rivermeadowApi } from './api';

export const bulkOperations = rivermeadowApi.injectEndpoints({
    endpoints: (builder) => ({
        // Generic bulk operation for adding tags to resources
        bulkAddTagsToResources: builder.mutation({
            queryFn: async (
                {
                    resources,
                    tags,
                    newTags = [], // Tags that need to be created first
                    orgId,
                    getResourceEndpoint,
                    updateResourceEndpoint,
                    // Optional configurable payload structure
                    updateResourcePayload = null,
                    updateMethod = 'PUT',
                    includeMoveGroupInfo,
                    updateTagsByQueryParameter = false,
                },
                { dispatch },
            ) => {
                try {
                    // Step 1: Create any new tags first (like backbone TagsComponent.getTags())
                    if (newTags.length > 0 && orgId) {
                        const tagModel = {
                            url: `/api/v3/organizations/${orgId}/tags`,
                            tags: newTags,
                        };

                        const createTagsResponse = await fetch(tagModel.url, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Accept: 'application/json',
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                            credentials: 'include',
                            body: JSON.stringify({ tags: newTags }),
                        });

                        if (!createTagsResponse.ok) {
                            throw new Error(`Failed to create new tags: ${newTags.join(', ')}`);
                        }
                    }

                    // Step 2: Update each resource (following backbone bulkAddTags logic)
                    const results = [];
                    const errors = [];

                    for (const resource of resources) {
                        try {
                            const resourceId = typeof resource === 'object' ? resource.id : resource;

                            // Get current resource data (like backbone model.fetch())
                            const getResponse = await fetch(getResourceEndpoint(resourceId), {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Accept: 'application/json',
                                    'X-Requested-With': 'XMLHttpRequest',
                                },
                                credentials: 'include',
                            });

                            if (!getResponse.ok) {
                                errors.push(`Failed to get resource ${resourceId}`);
                                continue;
                            }

                            const resourceData = await getResponse.json();
                            const existingTags = resourceData.tags || [];
                            // Combine existing tags with new tags (like backbone tags.concat(tagsToAdd))
                            const combinedTags = [...new Set([...existingTags, ...tags])];

                            // Use custom payload structure if provided, otherwise use simple tags payload like backbone
                            const updatePayload = updateResourcePayload
                                ? updateResourcePayload(resourceData, combinedTags)
                                : { tags: combinedTags };

                            let updateResourceUrl = updateResourceEndpoint(resourceId);
                            if (updateTagsByQueryParameter) {
                                updateResourceUrl = `${updateResourceUrl}?tags=${combinedTags.join(',')}`
                            }
                            if (includeMoveGroupInfo && resourceData?.move_group?.id) {
                                updateResourceUrl = `${updateResourceUrl}&move_group_id=${resourceData.move_group.id}`;
                                updatePayload.move_group_id = resourceData.move_group.id;
                            }

                            const updateResponse = await fetch(updateResourceUrl, {
                                method: updateMethod,
                                headers: {
                                    'Content-Type': 'application/json',
                                    Accept: 'application/json',
                                    'X-Requested-With': 'XMLHttpRequest',
                                },
                                credentials: 'include',
                                body: !updateTagsByQueryParameter? JSON.stringify(updatePayload) : undefined,
                            });

                            if (!updateResponse.ok) {
                                errors.push(`Failed to update resource ${resourceId}`);
                            } else {
                                results.push(await updateResponse.json());
                            }
                        } catch (error) {
                            errors.push(`Resource ${resourceId}: ${error.message}`);
                        }
                    }

                    if (errors.length > 0) {
                        throw new Error(`Some operations failed: ${errors.join(', ')}`);
                    }

                    return { data: { success: true, updated_resources: results.length } };
                } catch (error) {
                    return { error: error.message };
                }
            },
            invalidatesTags: ['SOURCES', 'APPLIANCES', 'TAGS'], // Invalidate multiple resource types
        }),
        // Generic bulk operation for assigning move groups to resources
        bulkAssignMoveGroupToResources: builder.mutation({
            queryFn: async (
                {
                    resources,
                    moveGroupData,
                    orgId,
                    createMoveGroupEndpoint,
                    updateResourceEndpoint,
                    // Optional configurable payload structures
                    createMoveGroupPayload = null,
                    updateResourcePayload = null,
                },
                { dispatch },
            ) => {
                try {
                    let moveGroupId;

                    if (moveGroupData.mode === 'existing') {
                        moveGroupId = moveGroupData.selectedMoveGroupId;
                    } else if (moveGroupData.mode === 'new') {
                        // Use custom payload structure if provided, otherwise use default
                        const createPayload = createMoveGroupPayload
                            ? createMoveGroupPayload(moveGroupData, orgId)
                            : {
                                  name: moveGroupData.newMoveGroupName.trim(),
                                  target_migration_date: moveGroupData.targetMigrationDate
                                      ? moveGroupData.targetMigrationDate.format('YYYY-MM-DD')
                                      : null,
                                  assigned_resource_id: moveGroupData.assignedResourceId || null,
                                  organization_id: orgId,
                              };

                        const createResponse = await fetch(createMoveGroupEndpoint(), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Accept: 'application/json',
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                            credentials: 'include',
                            body: JSON.stringify(createPayload),
                        });

                        if (!createResponse.ok) {
                            throw new Error('Failed to create move group');
                        }

                        const newMoveGroup = await createResponse.json();
                        moveGroupId = newMoveGroup.id;
                    } else {
                        throw new Error('Invalid move group mode');
                    }

                    // Update each resource with move group
                    const results = [];
                    const errors = [];

                    for (const resource of resources) {
                        try {
                            const resourceId = typeof resource === 'object' ? resource.id : resource;

                            // Use custom payload structure if provided, otherwise use default
                            const updatePayload = updateResourcePayload
                                ? updateResourcePayload(resourceId, moveGroupId)
                                : {
                                      id: resourceId,
                                      move_group_id: moveGroupId,
                                  };

                            const response = await fetch(updateResourceEndpoint(resourceId), {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Accept: 'application/json',
                                    'X-Requested-With': 'XMLHttpRequest',
                                },
                                credentials: 'include',
                                body: JSON.stringify(updatePayload),
                            });

                            if (!response.ok) {
                                errors.push(`Failed to update resource ${resourceId}`);
                            } else {
                                results.push(await response.json());
                            }
                        } catch (error) {
                            errors.push(`Resource ${resourceId}: ${error.message}`);
                        }
                    }

                    if (errors.length > 0) {
                        throw new Error(`Some operations failed: ${errors.join(', ')}`);
                    }

                    return {
                        data: {
                            success: true,
                            updated_resources: results.length,
                            move_group_id: moveGroupId,
                        },
                    };
                } catch (error) {
                    return { error: error.message };
                }
            },
            invalidatesTags: ['SOURCES', 'APPLIANCES', 'MOVEGROUPS'], // Invalidate multiple resource types
        }),
    }),
});

export const { useBulkAddTagsToResourcesMutation, useBulkAssignMoveGroupToResourcesMutation } = bulkOperations;
